import _ from 'lodash';

import { createContext, useContext, useMemo } from 'react';
import {
  generatePath,
  matchPath,
  matchRoutes,
  Navigate,
  PathMatch,
  RouteObject,
  useLocation,
  useNavigate,
  useParams
} from 'react-router';

export function matchRoutePath(
  pattern: Parameters<typeof matchPath>[0],
  pathname: Parameters<typeof matchPath>[1]
): PathMatch<string> | null {
  const match = matchPath(pattern, pathname);
  if (!match) {
    return null;
  }
  const decodedParams: Record<string, string | undefined> = {};
  for (const key of Object.keys(match.params)) {
    const value = (match.params as Record<string, string | undefined>)[key];
    decodedParams[key] = value !== undefined ? decodeURIComponent(value) : undefined;
  }
  return { ...match, params: decodedParams as PathMatch<string>['params'] };
}
import { SUPPORTED_LANGUAGE_PATH_ROOTS } from '../../../common/localeShared';
import { TServerPreloadActions } from '../../types/ssr';

type TComponent = React.ComponentType<any> & { serverPreloadActions?: TServerPreloadActions };

export type LoadableComponent = {
  load: () => Promise<{
    default: TComponent;
  }>;
};

export type TRouteComponent = TComponent | LoadableComponent;

export type TComponentRouteDef = {
  component?: TRouteComponent;
  path: string;
  exact?: boolean;
  strict?: boolean;
  index?: boolean;
  fallback?: React.ComponentType<any>;
  children?: (TComponentRouteDef | TRedirectRouteDef)[];
};

export type TRedirectRouteDef = {
  exact?: boolean;
  from: string;
  to: string;
};

export type TRouteDef = TComponentRouteDef | TRedirectRouteDef;

const useRouterInfo = () => {
  const location = useLocation();
  const params = useParams();

  const { routes } = useContext(RoutesProvider);

  const matches = matchRoutes(routes, location);
  const lastMatch = _.last(matches);

  const match = useMemo(() => {
    return {
      path: lastMatch?.route.path,
      url: lastMatch?.pathname,
      params
    };
  }, [lastMatch, params]);

  return {
    location,
    match
  };
};

export const withRouter = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const { location, match } = useRouterInfo();
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} location={location} match={match} />;
  };
};

export const isRedirectRoute = (route: TRouteDef): route is TRedirectRouteDef => {
  return 'from' in route;
};

export const isComponentRoute = (route: TRouteDef): route is TComponentRouteDef => {
  return !('from' in route);
};

export const RoutesProvider = createContext<{ routes: RouteObject[] }>(null as unknown as { routes: RouteObject[] });

export const useRoutes = () => {
  const context = useContext(RoutesProvider);

  if (!context) {
    throw new Error('useRoutes must be used within a RoutesProvider');
  }

  return context;
};

export const localizeRoutes = (routes: TRouteDef[]): TRouteDef[] => {
  const localePathPrefixes = SUPPORTED_LANGUAGE_PATH_ROOTS.map((code) => (code ? `/${code}` : ''));

  function localizeRoute(route: TRouteDef, prefix: string): TRouteDef {
    if (isRedirectRoute(route)) {
      return {
        ...route,
        from: prefix + route.from,
        to: prefix + route.to
      };
    } else if ('path' in route) {
      const children = (route as TComponentRouteDef).children?.map((child) => localizeRoute(child, prefix));
      return {
        ...route,
        path: prefix + route.path,
        children
      };
    }
    return route;
  }

  let localized: TRouteDef[] = [];

  for (const localePrefix of localePathPrefixes) {
    localized.push(...routes.map((route) => localizeRoute(route, localePrefix)));
  }

  localized.push(...routes);

  const redirectLangs = SUPPORTED_LANGUAGE_PATH_ROOTS.filter((root) => root !== '');
  for (const root of redirectLangs) {
    localized.unshift({
      from: `/${root}`,
      to: `/${root}/`,
      strict: true,
      exact: true
    });
  }

  return localized;
};

const ParamRedirect = ({ to }: { to: string }) => {
  const params = useParams();
  return <Navigate to={generatePath(to, params)} replace={true} />;
};

export const mapRoutesToReactRouterRoutes = (
  routes: TRouteDef[],
  { useLoader = false }: { useLoader?: boolean } = {}
): RouteObject[] => {
  const result = routes.map<RouteObject>((route) => {
    if (isRedirectRoute(route)) {
      const hasParams = route.to.includes(':');
      return {
        path: route.from,
        element: hasParams ? <ParamRedirect to={route.to} /> : <Navigate to={route.to} />
      };
    }

    const loaderData = useLoader
      ? {
          loader: () => {
            return {};
          }
        }
      : {};

    if (!route.children) {
      return {
        path: route.path,
        element: route.component ? <RouteComponentWrapper component={route.component} /> : null,
        index: route.index,
        ...loaderData
      };
    }

    return {
      path: route.path,
      element: route.component ? <RouteComponentWrapper component={route.component} /> : null,
      children: route.children ? mapRoutesToReactRouterRoutes(route.children, { useLoader }) : undefined,
      ...loaderData
    };
  });

  return result;
};

export type RouteMatchResult = {
  route: TComponentRouteDef;
  match: PathMatch<string>;
};

export const getRoutesMatches = (routes: TRouteDef[], testPath: string): RouteMatchResult[] => {
  const dfs = (routeList: TRouteDef[], parentChain: RouteMatchResult[]): RouteMatchResult[] => {
    for (const route of routeList) {
      if (isRedirectRoute(route)) {
        continue;
      }
      const { path } = route;
      const prefixMatch = matchPath({ path, end: false }, testPath);
      if (!prefixMatch) {
        continue;
      }
      const newChain = [...parentChain, { route, match: prefixMatch }];
      if ('children' in route && route.children) {
        const deep = dfs(route.children, newChain);
        if (deep.length > 0) {
          return deep;
        }
      }
      const strictMatch = matchPath({ path, end: true }, testPath);
      if (strictMatch) {
        return [...parentChain, { route, match: strictMatch }];
      }
    }
    return [];
  };

  return dfs(routes, []);
};

const RouteComponentWrapper = ({ component: Component }: { component: any }) => {
  const { location, match } = useRouterInfo();
  return <Component location={location} match={match} />;
};
